import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Archive, FolderPlus, Folder, FolderOpen, FileText, Plus, Trash2, 
  Download, ArrowLeft, Loader2, File, Calendar, User, X, Eye,
  ChevronRight, Home, FilePlus, Users, Search, Pencil, Bell, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const WorkFiles = () => {
  // Current user
  const currentUser = JSON.parse(localStorage.getItem('fortis_user') || '{}');
  const currentUserId = currentUser.id;
  
  // State - Hiyerarşik navigasyon
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = kök klasör
  const [breadcrumb, setBreadcrumb] = useState([]); // [{id, name}, ...]
  const [subfolders, setSubfolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showEditFolderModal, setShowEditFolderModal] = useState(false);
  const [showNewDocumentModal, setShowNewDocumentModal] = useState(false);
  const [showDocumentDetailModal, setShowDocumentDetailModal] = useState(false);
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [folderToEdit, setFolderToEdit] = useState(null);
  
  // Form states
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderDescription, setNewFolderDescription] = useState('');
  const [editFolderName, setEditFolderName] = useState('');
  const [editFolderDescription, setEditFolderDescription] = useState('');
  const [newDocName, setNewDocName] = useState('');
  const [newDocDescription, setNewDocDescription] = useState('');
  const [newDocFiles, setNewDocFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // User selection states for folder access
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Tüm kullanıcıları yükle
  const loadAllUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${API}/users/all`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data || []);
      }
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
    }
    setLoadingUsers(false);
  }, []);

  // Modal açıldığında kullanıcıları yükle
  useEffect(() => {
    if (showNewFolderModal) {
      loadAllUsers();
      setSelectedUsers([]);
      setUserSearchQuery('');
    }
  }, [showNewFolderModal, loadAllUsers]);

  // Edit modal açıldığında kullanıcıları yükle ve mevcut değerleri doldur
  useEffect(() => {
    if (showEditFolderModal && folderToEdit) {
      loadAllUsers();
      setEditFolderName(folderToEdit.name || '');
      setEditFolderDescription(folderToEdit.description || '');
      setSelectedUsers(folderToEdit.visible_to || []);
      setUserSearchQuery('');
    }
  }, [showEditFolderModal, folderToEdit, loadAllUsers]);

  // Klasör içeriğini yükle
  const loadFolderContent = useCallback(async (folderId) => {
    setLoading(true);
    try {
      if (folderId) {
        // Alt klasör içeriğini yükle
        const response = await fetch(`${API}/work/files/folders/${folderId}`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setCurrentFolder(data.folder || null);
          setBreadcrumb(data.breadcrumb || []);
          setSubfolders(data.subfolders || []);
          setDocuments(data.documents || []);
        } else {
          toast.error('Klasör içeriği yüklenemedi');
          setSubfolders([]);
          setDocuments([]);
        }
      } else {
        // Kök klasörleri yükle
        const response = await fetch(`${API}/work/files/folders`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setCurrentFolder(null);
          setBreadcrumb([]);
          setSubfolders(Array.isArray(data) ? data : []);
          setDocuments([]);
        } else {
          toast.error('Klasörler yüklenemedi');
          setSubfolders([]);
          setDocuments([]);
        }
      }
    } catch (error) {
      console.error('Yükleme hatası:', error);
      toast.error('İçerik yüklenemedi');
      setSubfolders([]);
      setDocuments([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadFolderContent(currentFolderId);
  }, [currentFolderId, loadFolderContent]);

  // Klasöre git
  const navigateToFolder = (folderId) => {
    setCurrentFolderId(folderId);
  };

  // Breadcrumb'dan navigasyon
  const navigateToBreadcrumb = (index) => {
    if (index === -1) {
      // Ana dizine dön
      setCurrentFolderId(null);
    } else {
      setCurrentFolderId(breadcrumb[index].id);
    }
  };

  // Yeni klasör oluştur
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Klasör adı gerekli');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/work/files/folders`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newFolderName.trim(),
          description: newFolderDescription.trim() || null,
          parent_id: currentFolderId, // Null ise kök klasör olur
          visible_to: selectedUsers.length > 0 ? selectedUsers : null
        })
      });

      if (response.ok) {
        toast.success('Klasör oluşturuldu');
        setShowNewFolderModal(false);
        setNewFolderName('');
        setNewFolderDescription('');
        setSelectedUsers([]);
        loadFolderContent(currentFolderId);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Klasör oluşturulamadı');
      }
    } catch (error) {
      console.error('Klasör oluşturma hatası:', error);
      toast.error('Klasör oluşturulamadı');
    }
    setSubmitting(false);
  };

  // Klasör düzenle
  const handleEditFolder = async () => {
    if (!folderToEdit || !editFolderName.trim()) {
      toast.error('Klasör adı gerekli');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/work/files/folders/${folderToEdit.id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editFolderName.trim(),
          description: editFolderDescription.trim() || null,
          visible_to: selectedUsers.length > 0 ? selectedUsers : []
        })
      });

      if (response.ok) {
        toast.success('Klasör güncellendi');
        setShowEditFolderModal(false);
        setFolderToEdit(null);
        setEditFolderName('');
        setEditFolderDescription('');
        setSelectedUsers([]);
        loadFolderContent(currentFolderId);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Klasör güncellenemedi');
      }
    } catch (error) {
      console.error('Klasör güncelleme hatası:', error);
      toast.error('Klasör güncellenemedi');
    }
    setSubmitting(false);
  };

  // Klasör sil
  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/work/files/folders/${folderToDelete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Klasör silindi');
        setShowDeleteFolderModal(false);
        setFolderToDelete(null);
        loadFolderContent(currentFolderId);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Klasör silinemedi');
      }
    } catch (error) {
      console.error('Klasör silme hatası:', error);
      toast.error('Klasör silinemedi');
    }
    setSubmitting(false);
  };

  // Yeni belge oluştur
  const handleCreateDocument = async () => {
    if (!currentFolderId) {
      toast.error('Önce bir klasör seçin');
      return;
    }
    if (!newDocName.trim()) {
      toast.error('Belge adı gerekli');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', newDocName.trim());
      if (newDocDescription.trim()) {
        formData.append('description', newDocDescription.trim());
      }
      newDocFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API}/work/files/folders/${currentFolderId}/documents`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      if (response.ok) {
        toast.success('Belge eklendi');
        setShowNewDocumentModal(false);
        setNewDocName('');
        setNewDocDescription('');
        setNewDocFiles([]);
        loadFolderContent(currentFolderId);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Belge eklenemedi');
      }
    } catch (error) {
      console.error('Belge ekleme hatası:', error);
      toast.error('Belge eklenemedi');
    }
    setSubmitting(false);
  };

  // Belge sil
  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Bu belgeyi silmek istediğinize emin misiniz?')) return;

    try {
      const response = await fetch(`${API}/work/files/documents/${docId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Belge silindi');
        loadFolderContent(currentFolderId);
        if (selectedDocument?.id === docId) {
          setShowDocumentDetailModal(false);
          setSelectedDocument(null);
        }
      } else {
        toast.error('Belge silinemedi');
      }
    } catch (error) {
      console.error('Belge silme hatası:', error);
      toast.error('Belge silinemedi');
    }
  };

  // Belgeyi görüldü olarak işaretle
  const markDocumentAsSeen = async (docId) => {
    try {
      await fetch(`${API}/work/files/documents/${docId}/mark-seen`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      loadFolderContent(currentFolderId);
    } catch (error) {
      console.error('Belge görüldü işaretleme hatası:', error);
    }
  };

  // Belge detayını görüntüle
  const handleViewDocument = (doc) => {
    setSelectedDocument(doc);
    setShowDocumentDetailModal(true);
    if (doc.is_new) {
      markDocumentAsSeen(doc.id);
    }
  };

  // Dosya indir
  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const response = await fetch(`${API}/work/files/files/${fileId}/download`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        toast.error('Dosya indirilemedi');
      }
    } catch (error) {
      console.error('İndirme hatası:', error);
      toast.error('Dosya indirilemedi');
    }
  };

  // Dosya ikonunu belirle
  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(ext)) return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['xls', 'xlsx'].includes(ext)) return '📊';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼️';
    if (['zip', 'rar', '7z'].includes(ext)) return '📦';
    return '📎';
  };

  // Tarih formatla
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="work-files">
      {/* Header */}
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Archive className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-xl text-blue-900">Dosyalar</CardTitle>
                <p className="text-sm text-blue-600 mt-1">İş dosyalarınızı yönetin</p>
              </div>
            </div>
            <div className="flex gap-2">
              {/* Klasör oluşturma: Ana dizinde herkes, alt klasörde sadece klasör sahibi */}
              {(!currentFolderId || (currentFolder && currentFolder.created_by === currentUserId)) && (
                <Button
                  onClick={() => setShowNewFolderModal(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="create-folder-btn"
                >
                  <FolderPlus className="h-4 w-4 mr-2" />
                  Yeni Klasör
                </Button>
              )}
              {currentFolderId && (
                <Button
                  onClick={() => setShowNewDocumentModal(true)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="create-document-btn"
                >
                  <FilePlus className="h-4 w-4 mr-2" />
                  Belge Ekle
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Breadcrumb Navigasyon */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center gap-2 text-sm">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateToBreadcrumb(-1)}
              className={`flex items-center gap-1 ${!currentFolderId ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-blue-600'}`}
              data-testid="breadcrumb-home"
            >
              <Home className="h-4 w-4" />
              Ana Dizin
            </Button>
            {breadcrumb.map((item, index) => (
              <React.Fragment key={item.id}>
                <ChevronRight className="h-4 w-4 text-gray-400" />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigateToBreadcrumb(index)}
                  className={`flex items-center gap-1 ${index === breadcrumb.length - 1 ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-blue-600'}`}
                  data-testid={`breadcrumb-${item.id}`}
                >
                  <Folder className="h-4 w-4" />
                  {item.name}
                </Button>
              </React.Fragment>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* İçerik */}
      <Card>
        <CardContent className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Yükleniyor...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Alt Klasörler */}
              {subfolders.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Folder className="h-4 w-4" />
                    Klasörler ({subfolders.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {subfolders.map((folder) => (
                      <div
                        key={folder.id}
                        className="group relative border rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-all cursor-pointer"
                        data-testid={`folder-${folder.id}`}
                      >
                        {/* Yeni belge badge'i */}
                        {folder.new_document_count > 0 && (
                          <div className="absolute -top-2 -right-2 z-10">
                            <Badge className="bg-red-500 text-white text-xs px-2 py-0.5 flex items-center gap-1 animate-pulse">
                              <Sparkles className="h-3 w-3" />
                              {folder.new_document_count} yeni
                            </Badge>
                          </div>
                        )}
                        <div 
                          className="flex items-start gap-3"
                          onClick={() => navigateToFolder(folder.id)}
                        >
                          <div className="p-2 bg-amber-100 rounded-lg group-hover:bg-amber-200 transition-colors">
                            <FolderOpen className="h-6 w-6 text-amber-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 truncate">{folder.name}</h4>
                            {folder.description && (
                              <p className="text-xs text-gray-500 truncate mt-1">{folder.description}</p>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                              <span className="flex items-center gap-1">
                                <Folder className="h-3 w-3" />
                                {folder.subfolder_count || 0}
                              </span>
                              <span className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {folder.document_count || 0}
                              </span>
                            </div>
                            {/* Oluşturan bilgisi */}
                            {folder.created_by_name && (
                              <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                                <User className="h-3 w-3" />
                                <span>Oluşturan: {folder.created_by_name}</span>
                              </div>
                            )}
                            {/* Görüntüleyenler bilgisi */}
                            {folder.visible_to_names && folder.visible_to_names.length > 0 && (
                              <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                                <Users className="h-3 w-3" />
                                <span>Görüntüleyenler: {folder.visible_to_names.join(', ')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Düzenle butonu - sadece klasör sahibine göster (silme yok) */}
                        {folder.created_by === currentUserId && (
                          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                setFolderToEdit(folder);
                                setShowEditFolderModal(true);
                              }}
                              data-testid={`edit-folder-${folder.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Belgeler */}
              {documents.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Belgeler ({documents.length})
                  </h3>
                  <div className="space-y-2">
                    {documents.map((doc) => {
                      // İzin kontrolü: sadece belge sahibi silebilir
                      // İş Paneli'nde belge silme yetkisi yok - sadece Şube Yönetim Paneli'nde
                      const canDeleteDocument = false;
                      
                      return (
                        <div
                          key={doc.id}
                          className={`group flex items-center justify-between border rounded-lg p-3 hover:border-blue-300 hover:bg-blue-50 transition-all ${doc.is_new ? 'border-l-4 border-l-red-500 bg-red-50/30' : ''}`}
                          data-testid={`document-${doc.id}`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`relative p-2 rounded-lg ${doc.is_new ? 'bg-red-100' : 'bg-blue-100'}`}>
                              <FileText className={`h-5 w-5 ${doc.is_new ? 'text-red-600' : 'text-blue-600'}`} />
                              {doc.is_new && (
                                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-gray-900">{doc.name}</h4>
                                {doc.is_new && (
                                  <Badge className="bg-red-500 text-white text-xs px-1.5 py-0">
                                    YENİ
                                  </Badge>
                                )}
                              </div>
                              {doc.description && (
                                <p className="text-xs text-gray-500 truncate">{doc.description}</p>
                              )}
                              <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {formatDate(doc.created_at)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  Ekleyen: {doc.created_by_name || 'Bilinmiyor'}
                                </span>
                                {doc.files?.length > 0 && (
                                  <Badge variant="outline" className="text-xs">
                                    {doc.files.length} dosya
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDocument(doc)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`view-document-${doc.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {canDeleteDocument && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteDocument(doc.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-document-${doc.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Boş durum */}
              {subfolders.length === 0 && documents.length === 0 && (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                    <Archive className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {currentFolderId ? 'Bu klasör boş' : 'Henüz klasör yok'}
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {currentFolderId 
                      ? (currentFolder && currentFolder.created_by === currentUserId 
                          ? 'Alt klasör veya belge ekleyerek başlayın'
                          : 'Belge ekleyerek başlayın')
                      : 'Yeni klasör oluşturarak arşivinizi düzenleyin'}
                  </p>
                  <div className="flex justify-center gap-2">
                    {/* Ana dizinde veya kendi klasöründe klasör oluşturabilir */}
                    {(!currentFolderId || (currentFolder && currentFolder.created_by === currentUserId)) && (
                      <Button
                        onClick={() => setShowNewFolderModal(true)}
                        variant="outline"
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                      >
                        <FolderPlus className="h-4 w-4 mr-2" />
                        Klasör Oluştur
                      </Button>
                    )}
                    {currentFolderId && (
                      <Button
                        onClick={() => setShowNewDocumentModal(true)}
                        variant="outline"
                        className="border-green-300 text-green-600 hover:bg-green-50"
                      >
                        <FilePlus className="h-4 w-4 mr-2" />
                        Belge Ekle
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Yeni Klasör Modal */}
      <Dialog open={showNewFolderModal} onOpenChange={setShowNewFolderModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="h-5 w-5 text-blue-600" />
              Yeni Klasör Oluştur
            </DialogTitle>
            <DialogDescription>
              {currentFolderId 
                ? `"${currentFolder?.name}" klasörünün içine yeni klasör oluşturun`
                : 'Ana dizinde yeni klasör oluşturun'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="folderName">Klasör Adı *</Label>
              <Input
                id="folderName"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Klasör adını girin"
                className="mt-1"
                data-testid="folder-name-input"
              />
            </div>
            <div>
              <Label htmlFor="folderDescription">Açıklama</Label>
              <Textarea
                id="folderDescription"
                value={newFolderDescription}
                onChange={(e) => setNewFolderDescription(e.target.value)}
                placeholder="İsteğe bağlı açıklama"
                className="mt-1"
                rows={2}
                data-testid="folder-description-input"
              />
            </div>
            {/* Kullanıcı Seçimi */}
            <div>
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Kimler Görebilsin?
              </Label>
              <p className="text-xs text-gray-500 mb-2">
                Seçim yapmazsanız sadece siz görebilirsiniz
              </p>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Kullanıcı ara..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="user-search-input"
                />
              </div>
              <ScrollArea className="h-40 border rounded-md p-2">
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-1">
                    {allUsers
                      .filter(user => 
                        user.name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                        user.email?.toLowerCase().includes(userSearchQuery.toLowerCase())
                      )
                      .map(user => (
                        <div
                          key={user.id}
                          className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            if (selectedUsers.includes(user.id)) {
                              setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                            } else {
                              setSelectedUsers([...selectedUsers, user.id]);
                            }
                          }}
                        >
                          <Checkbox
                            checked={selectedUsers.includes(user.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedUsers([...selectedUsers, user.id]);
                              } else {
                                setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                              }
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{user.name}</p>
                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                          </div>
                          {user.branch_name && (
                            <Badge variant="outline" className="text-xs shrink-0">
                              {user.branch_name}
                            </Badge>
                          )}
                        </div>
                      ))}
                    {allUsers.filter(user => 
                      user.name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                      user.email?.toLowerCase().includes(userSearchQuery.toLowerCase())
                    ).length === 0 && (
                      <p className="text-center text-sm text-gray-500 py-4">
                        Kullanıcı bulunamadı
                      </p>
                    )}
                  </div>
                )}
              </ScrollArea>
              {selectedUsers.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {selectedUsers.map(userId => {
                    const user = allUsers.find(u => u.id === userId);
                    return user ? (
                      <Badge key={userId} variant="secondary" className="text-xs">
                        {user.name}
                        <X 
                          className="h-3 w-3 ml-1 cursor-pointer" 
                          onClick={() => setSelectedUsers(selectedUsers.filter(id => id !== userId))}
                        />
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowNewFolderModal(false);
                setNewFolderName('');
                setNewFolderDescription('');
                setSelectedUsers([]);
              }}
            >
              İptal
            </Button>
            <Button
              onClick={handleCreateFolder}
              disabled={submitting || !newFolderName.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-folder-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Oluşturuluyor...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Oluştur
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Klasör Silme Onay Modal */}
      <Dialog open={showDeleteFolderModal} onOpenChange={setShowDeleteFolderModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Klasörü Sil
            </DialogTitle>
            <DialogDescription>
              "{folderToDelete?.name}" klasörünü silmek istediğinize emin misiniz?
              <br />
              <strong className="text-red-600">Bu işlem klasördeki tüm alt klasörleri ve belgeleri de silecektir!</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteFolderModal(false);
                setFolderToDelete(null);
              }}
            >
              İptal
            </Button>
            <Button
              onClick={handleDeleteFolder}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-folder-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Siliniyor...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Sil
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Klasör Düzenleme Modal */}
      <Dialog open={showEditFolderModal} onOpenChange={setShowEditFolderModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Klasörü Düzenle
            </DialogTitle>
            <DialogDescription>
              "{folderToEdit?.name}" klasörünü düzenleyin
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="editFolderName">Klasör Adı *</Label>
              <Input
                id="editFolderName"
                value={editFolderName}
                onChange={(e) => setEditFolderName(e.target.value)}
                placeholder="Klasör adını girin"
                className="mt-1"
                data-testid="edit-folder-name-input"
              />
            </div>
            <div>
              <Label htmlFor="editFolderDescription">Açıklama</Label>
              <Textarea
                id="editFolderDescription"
                value={editFolderDescription}
                onChange={(e) => setEditFolderDescription(e.target.value)}
                placeholder="İsteğe bağlı açıklama"
                className="mt-1"
                rows={2}
                data-testid="edit-folder-description-input"
              />
            </div>
            {/* Kullanıcı Seçimi */}
            <div>
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Kimler Görebilsin?
              </Label>
              <p className="text-xs text-gray-500 mb-2">
                Seçim yapmazsanız sadece siz görebilirsiniz
              </p>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Kullanıcı ara..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <ScrollArea className="h-40 border rounded-md p-2">
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-1">
                    {allUsers
                      .filter(user => 
                        user.name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                        user.email?.toLowerCase().includes(userSearchQuery.toLowerCase())
                      )
                      .map(user => (
                        <div
                          key={user.id}
                          className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            if (selectedUsers.includes(user.id)) {
                              setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                            } else {
                              setSelectedUsers([...selectedUsers, user.id]);
                            }
                          }}
                        >
                          <Checkbox
                            checked={selectedUsers.includes(user.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedUsers([...selectedUsers, user.id]);
                              } else {
                                setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                              }
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{user.name}</p>
                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                          </div>
                          {user.branch_name && (
                            <Badge variant="outline" className="text-xs shrink-0">
                              {user.branch_name}
                            </Badge>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </ScrollArea>
              {selectedUsers.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {selectedUsers.map(userId => {
                    const user = allUsers.find(u => u.id === userId);
                    return user ? (
                      <Badge key={userId} variant="secondary" className="text-xs">
                        {user.name}
                        <X 
                          className="h-3 w-3 ml-1 cursor-pointer" 
                          onClick={() => setSelectedUsers(selectedUsers.filter(id => id !== userId))}
                        />
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditFolderModal(false);
                setFolderToEdit(null);
                setEditFolderName('');
                setEditFolderDescription('');
                setSelectedUsers([]);
              }}
            >
              İptal
            </Button>
            <Button
              onClick={handleEditFolder}
              disabled={submitting || !editFolderName.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-edit-folder-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Güncelleniyor...
                </>
              ) : (
                <>
                  <Pencil className="h-4 w-4 mr-2" />
                  Güncelle
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Yeni Belge Modal */}
      <Dialog open={showNewDocumentModal} onOpenChange={setShowNewDocumentModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FilePlus className="h-5 w-5 text-green-600" />
              Belge Ekle
            </DialogTitle>
            <DialogDescription>
              "{currentFolder?.name}" klasörüne yeni belge ekleyin
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="docName">Belge Adı *</Label>
              <Input
                id="docName"
                value={newDocName}
                onChange={(e) => setNewDocName(e.target.value)}
                placeholder="Belge adını girin"
                className="mt-1"
                data-testid="document-name-input"
              />
            </div>
            <div>
              <Label htmlFor="docDescription">Açıklama</Label>
              <Textarea
                id="docDescription"
                value={newDocDescription}
                onChange={(e) => setNewDocDescription(e.target.value)}
                placeholder="İsteğe bağlı açıklama"
                className="mt-1"
                rows={3}
                data-testid="document-description-input"
              />
            </div>
            <div>
              <Label>Dosyalar</Label>
              <div className="mt-1 border-2 border-dashed rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={(e) => setNewDocFiles(Array.from(e.target.files || []))}
                  className="hidden"
                  id="docFileInput"
                  data-testid="document-file-input"
                />
                <label htmlFor="docFileInput" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-2">
                    <Plus className="h-8 w-8 text-gray-400" />
                    <span className="text-sm text-gray-600">Dosya seçmek için tıklayın</span>
                    <span className="text-xs text-gray-400">Birden fazla dosya seçebilirsiniz</span>
                  </div>
                </label>
              </div>
              {newDocFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {newDocFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                      <span className="text-sm truncate flex items-center gap-2">
                        <span>{getFileIcon(file.name)}</span>
                        {file.name}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setNewDocFiles(files => files.filter((_, i) => i !== index))}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowNewDocumentModal(false);
                setNewDocName('');
                setNewDocDescription('');
                setNewDocFiles([]);
              }}
            >
              İptal
            </Button>
            <Button
              onClick={handleCreateDocument}
              disabled={submitting || !newDocName.trim()}
              className="bg-green-600 hover:bg-green-700"
              data-testid="submit-document-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Ekleniyor...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Ekle
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Belge Detay Modal */}
      <Dialog open={showDocumentDetailModal} onOpenChange={setShowDocumentDetailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              {selectedDocument?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedDocument && (
            <div className="space-y-4 py-4">
              {selectedDocument.description && (
                <div>
                  <Label className="text-gray-500">Açıklama</Label>
                  <p className="mt-1 text-gray-900">{selectedDocument.description}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Oluşturan</Label>
                  <p className="mt-1 text-gray-900">{selectedDocument.created_by_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Oluşturma Tarihi</Label>
                  <p className="mt-1 text-gray-900">{formatDate(selectedDocument.created_at)}</p>
                </div>
              </div>
              {selectedDocument.files?.length > 0 && (
                <div>
                  <Label className="text-gray-500">Dosyalar ({selectedDocument.files.length})</Label>
                  <div className="mt-2 space-y-2">
                    {selectedDocument.files.map((file, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between bg-gray-50 rounded-lg p-3"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-lg">{getFileIcon(file.original_name || file.name)}</span>
                          <span className="text-sm truncate">{file.original_name || file.name}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadFile(file.id, file.original_name || file.name)}
                          className="ml-2 text-blue-600 hover:text-blue-700"
                          data-testid={`download-file-${file.id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          İndir
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDocumentDetailModal(false);
                setSelectedDocument(null);
              }}
            >
              Kapat
            </Button>
            {/* Belgeyi Sil - İş Paneli'nde kaldırıldı, sadece Şube Yönetim Paneli'nde mevcut */}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkFiles;
